import { useEffect, useState, useMemo } from "react";
import welcomeImg from "@/assets/welcome-portrait.jpeg";
import { api, publicApi, effectivePrice, formatTL } from "@/lib/api";
import { ProductCard } from "@/components/app/ProductCard";
import { CartDrawer } from "@/components/app/CartDrawer";
import { RemovablesModal } from "@/components/app/RemovablesModal";
import { ShoppingBag, Store, MapPin, ArrowRight, Search, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { LangContext, LangToggle, makeT, buildNote } from "@/lib/kioskI18n";

const TAX_RATE = 0.1;

const BranchesHub = ({ hub }) => {
  const branches = hub?.branches || [];
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const filtered = query
    ? branches.filter((b) =>
        [b.name, b.district, b.city, b.address]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
    : branches;

  return (
    <div data-testid="public-branches-hub" className="min-h-[calc(100vh-4rem)] bg-white">
      <div className="max-w-[900px] mx-auto px-5 sm:px-6 pt-10 pb-16">
        <p className="font-display italic text-[var(--fb-red)] text-3xl sm:text-4xl mb-1">Gel Al</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-[var(--fb-ink)]">
          SİPARİŞİNİ VER,
          <br />
          GELİP AL.
        </h1>
        <p className="text-[var(--fb-muted)] mt-4 text-sm sm:text-base max-w-xl">
          Şubeni seç, menüden siparişini oluştur, kredi kartıyla öde — geldiğinde hazır olsun.
        </p>

        <p className="font-display font-bold text-lg mt-10 mb-3">ŞUBENİ BUL</p>
        <div className="relative mb-5">
          <Search className="w-5 h-5 text-[var(--fb-muted)] absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="hub-branch-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Şube adı, ilçe veya şehir ara... (örn: Atakum)"
            className="w-full bg-white border-2 border-[var(--fb-border)] rounded-2xl pl-12 pr-4 py-4 text-sm outline-none focus:border-[var(--fb-red)] transition-colors"
          />
        </div>

        <div className="space-y-3">
          {filtered.length === 0 ? (
            <p className="text-center text-[var(--fb-muted)] py-10">Aramanızla eşleşen şube bulunamadı.</p>
          ) : (
            filtered.map((b) => (
              <a
                key={b.code}
                href={`/?sube=${b.code}`}
                data-testid={`hub-branch-${b.code}`}
                className="group flex items-center gap-4 bg-white border-2 border-[var(--fb-border)] rounded-2xl p-4 hover:border-[var(--fb-red)] hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-red-50 grid place-items-center shrink-0">
                  <Store className="w-6 h-6 text-[var(--fb-red)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-base sm:text-lg leading-tight truncate">
                    {b.name}
                    {(b.district || b.city) && (
                      <span className="text-[var(--fb-muted)] font-sans font-normal text-sm">
                        {" "}— {[b.district, b.city].filter(Boolean).join(" / ")}
                      </span>
                    )}
                  </p>
                  {b.address && (
                    <p className="text-xs text-[var(--fb-muted)] truncate mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 shrink-0 text-[var(--fb-red)]" />
                      {b.address}
                    </p>
                  )}
                </div>
                <ArrowRight className="w-5 h-5 text-[var(--fb-muted)] group-hover:text-[var(--fb-red)] group-hover:translate-x-1 transition-all shrink-0" />
              </a>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default function Kiosk({ user, publicCode }) {
  const [products, setProducts] = useState([]);
  const [lang, setLang] = useState("tr");
  const [menuT, setMenuT] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fb_menu_en") || "{}");
    } catch (_) {
      return {};
    }
  });
  const { t, m } = makeT(lang, menuT);
  const [activeCat, setActiveCat] = useState("Tüm Ürünler");
  const [discount, setDiscount] = useState(null);
  const [welcomeRetry, setWelcomeRetry] = useState(0);
  const [welcomeFailed, setWelcomeFailed] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const orderType = publicCode ? "Link" : "Gel-Al";
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(Boolean(publicCode));
  const [cats, setCats] = useState(["Tüm Ürünler"]);
  const [removalProduct, setRemovalProduct] = useState(null);
  const [pubBranch, setPubBranch] = useState(null);
  const [pubHub, setPubHub] = useState(null);
  const [pubError, setPubError] = useState(false);
  const [payResult, setPayResult] = useState(null); // "basarili" | "basarisiz"
  const [payOrderNo, setPayOrderNo] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("odeme");
    if (p === "basarili" || p === "basarisiz") {
      setPayResult(p);
      setPayOrderNo(params.get("no") || "");
      const url = new URL(window.location.href);
      url.searchParams.delete("odeme");
      url.searchParams.delete("no");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const client = publicCode ? publicApi : api;
  const branchInfo = publicCode ? pubBranch : user;
  const location = [branchInfo?.city, branchInfo?.district].filter(Boolean).join(" / ");
  const isClosed = branchInfo?.is_active === false;

  const loadMenu = async () => {
    setLoading(true);
    client
      .get("/menu-translations")
      .then((r) => {
        setMenuT(r.data || {});
        localStorage.setItem("fb_menu_en", JSON.stringify(r.data || {}));
      })
      .catch(() => {});
    try {
      const [p, c] = await Promise.all([client.get("/products"), client.get("/categories")]);
      setProducts(p.data.filter((x) => x.active !== false));
      setCats(["Tüm Ürünler", ...c.data.map((x) => x.name)]);
    } catch (e) {
      toast.error(t("Menü yüklenemedi"));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (publicCode || !started) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setRemovalProduct(null);
        setCartOpen(false);
        setCart([]);
        setDiscount(null);
        setActiveCat("Tüm Ürünler");
        setStarted(false);
        setLang("tr");
        window.scrollTo(0, 0);
      }, 60000);
    };
    const events = ["pointerdown", "touchstart", "keydown", "scroll", "wheel"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true, capture: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset, { capture: true }));
    };
  }, [started, publicCode]);

  useEffect(() => {
    if (!publicCode) return;
    publicApi
      .get(`/public/branch/${publicCode}`)
      .then((r) => {
        if (r.data?.is_hub) setPubHub(r.data);
        else setPubBranch(r.data);
      })
      .catch(() => setPubError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicCode]);

  const filtered = useMemo(() => {
    if (activeCat === "Tüm Ürünler") return products;
    return products.filter((p) => p.category === activeCat);
  }, [products, activeCat]);

  const addToCart = (product, removals = [], added = [], drink = null) => {
    const opts = { removals, added: added.map((a) => a.name), drink: drink?.name || "" };
    const note = buildNote(opts, (s) => s, (s) => s);
    const extraTotal = added.reduce((s, a) => s + (a.price || 0), 0);
    const drinkTotal = drink ? drink.price || 0 : 0;
    const price = effectivePrice(product) + extraTotal + drinkTotal;
    const uid = `${product.id}::${note}`;
    setCart((prev) => {
      const found = prev.find((i) => i.uid === uid);
      if (found) return prev.map((i) => (i.uid === uid ? { ...i, quantity: i.quantity + 1 } : i));
      return [...prev, { uid, product_id: product.id, name: product.name, price, quantity: 1, note, opts }];
    });
    toast.success(`${m(product.name)} ${t("sepete eklendi")}`);
  };

  const handleAddRequest = (product) => {
    const hasRemovables = product.removables && product.removables.length;
    const hasExtras = product.extras && product.extras.length;
    const hasDrinks = product.drinks && product.drinks.length;
    if (hasRemovables || hasExtras || hasDrinks) setRemovalProduct(product);
    else addToCart(product);
  };

  const updateQty = (uid, delta) => {
    setCart((prev) =>
      prev.map((i) => (i.uid === uid ? { ...i, quantity: i.quantity + delta } : i)).filter((i) => i.quantity > 0)
    );
  };
  const updateNote = (uid, note) => setCart((prev) => prev.map((i) => (i.uid === uid ? { ...i, note, opts: null } : i)));

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const gross = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const discountAmount = discount
    ? Math.min(gross, Math.round((discount.type === "percent" ? (gross * discount.value) / 100 : discount.value) * 100) / 100)
    : 0;
  const total = gross - discountAmount;
  const tax = total - total / (1 + TAX_RATE);
  const subtotal = total - tax;

  const applyCode = async (code) => {
    const r = await client.post("/discount-codes/validate", { code });
    setDiscount(r.data);
    return r.data;
  };

  const placeOrder = async (paymentMethod, customer) => {
    const payload = {
      items: cart.map(({ opts, ...i }) => i),
      order_type: orderType,
      payment_method: paymentMethod,
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      total: Number(total.toFixed(2)),
      discount_code: discount && !publicCode ? discount.code : "",
      discount_amount: Number(discountAmount.toFixed(2)),
      customer_name: customer?.name || "",
      customer_phone: customer?.phone || "",
      customer_address: customer?.address || "",
    };
    const body = publicCode ? { ...payload, branch_code: publicCode } : payload;
    const res = await client.post("/orders", body);
    return res.data;
  };

  if (publicCode && pubError) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-[var(--fb-neutral)] grid place-items-center px-4 py-10">
        <div data-testid="kiosk-invalid-link" className="w-full max-w-md bg-white rounded-2xl border border-[var(--fb-border)] p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 grid place-items-center mx-auto mb-4">
            <Store className="w-8 h-8 text-[var(--fb-red)]" />
          </div>
          <h1 className="font-display font-extrabold text-2xl">Menü Bulunamadı</h1>
          <p className="text-sm text-[var(--fb-muted)] mt-2">Sipariş bağlantısı geçersiz. Lütfen işletmeyle iletişime geçin.</p>
        </div>
      </div>
    );
  }
  if (payResult) {
    const ok = payResult === "basarili";
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-[var(--fb-neutral)] grid place-items-center px-4 py-10">
        <div
          data-testid="kiosk-payment-result"
          className="w-full max-w-md bg-white rounded-2xl border border-[var(--fb-border)] p-8 text-center animate-fade-up"
        >
          {ok ? (
            <>
              <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-4 animate-pop" />
              <h1 className="font-display font-extrabold text-2xl">Ödemeniz Alındı!</h1>
              {payOrderNo && (
                <div
                  data-testid="payment-result-order-no"
                  className="mt-4 inline-block bg-[var(--fb-neutral)] border border-[var(--fb-border)] rounded-xl px-5 py-2.5"
                >
                  <span className="text-xs text-[var(--fb-muted)] block">Sipariş Numaranız</span>
                  <span className="font-display font-extrabold text-3xl text-[var(--fb-red)]">#{payOrderNo}</span>
                </div>
              )}
              <p className="text-sm text-[var(--fb-muted)] mt-3">
                Siparişiniz ödemesi başarıyla tamamlandı ve mutfağa iletildi. Teşekkür ederiz!
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-red-50 grid place-items-center mx-auto mb-4">
                <X className="w-8 h-8 text-[var(--fb-red)]" />
              </div>
              <h1 className="font-display font-extrabold text-2xl">Ödeme Tamamlanamadı</h1>
              <p className="text-sm text-[var(--fb-muted)] mt-2">
                Ödeme işlemi başarısız oldu veya iptal edildi. Lütfen tekrar deneyin.
              </p>
            </>
          )}
          <button
            data-testid="payment-result-back-btn"
            onClick={() => setPayResult(null)}
            className="mt-6 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold px-6 py-3 rounded-full transition-colors"
          >
            {ok ? "Menüye Dön" : "Tekrar Dene"}
          </button>
        </div>
      </div>
    );
  }
  if (publicCode && pubHub) {
    return <BranchesHub hub={pubHub} />;
  }
  if (publicCode && !pubBranch && !pubHub) {
    return <div className="min-h-[calc(100vh-4rem)] grid place-items-center text-[var(--fb-muted)]">Yükleniyor...</div>;
  }

  if (isClosed) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-[var(--fb-neutral)] grid place-items-center px-4 py-10">
        <div data-testid="kiosk-branch-closed" className="w-full max-w-md bg-white rounded-2xl border border-[var(--fb-border)] p-8 text-center animate-fade-up">
          <div className="w-16 h-16 rounded-full bg-red-50 grid place-items-center mx-auto mb-4">
            <Store className="w-8 h-8 text-[var(--fb-red)]" />
          </div>
          <h1 className="font-display font-extrabold text-2xl">Şube Kapalı</h1>
          <p className="text-sm text-[var(--fb-muted)] mt-2">
            {branchInfo?.name ? `"${branchInfo.name}" ` : "Bu şube "}şu anda hizmet vermiyor. Lütfen daha sonra tekrar deneyin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <LangContext.Provider value={{ lang, menu: menuT }}>
    <div lang={lang} className="min-h-[calc(100vh-4rem)] bg-[var(--fb-offwhite)]">
      {!publicCode && !started && (
        <div className="fixed top-20 right-4 z-[45]">
          <LangToggle lang={lang} setLang={setLang} />
        </div>
      )}
      {/* Karşılama ekranı */}
      {!publicCode && !started && (
        <button
          data-testid="kiosk-welcome-screen"
          onClick={() => {
            setActiveCat("Tüm Ürünler");
            window.scrollTo(0, 0);
            setStarted(true);
          }}
          className="fixed top-16 left-0 right-0 bottom-0 z-40 w-full flex flex-col items-center justify-end overflow-hidden cursor-pointer bg-[#C8102E] pb-8 px-6"
        >
          <img
            key={welcomeRetry}
            data-testid="kiosk-welcome-image"
            src={welcomeRetry ? `${welcomeImg}?r=${welcomeRetry}` : welcomeImg}
            alt=""
            onLoad={() => setWelcomeFailed(false)}
            onError={() => {
              setWelcomeFailed(true);
              setTimeout(() => setWelcomeRetry((r) => r + 1), 3000);
            }}
            style={{ objectPosition: "center top", visibility: welcomeFailed ? "hidden" : "visible" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {welcomeFailed && (
            <div data-testid="kiosk-welcome-fallback" className="absolute inset-0 flex flex-col items-center justify-center gap-6 text-white px-8 text-center">
              <img src="/logo.jpeg" alt="Deny's Burger & Fries" className="w-48 h-48 object-contain rounded-3xl bg-white p-4 shadow-2xl" />
              <p className="font-display font-extrabold text-4xl sm:text-6xl leading-none">{t("Hoş geldiniz")}</p>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="relative flex flex-col items-center gap-3 w-full max-w-[640px]">
            <span className="w-full justify-center animate-cart-glow inline-flex items-center gap-3 bg-white text-[var(--fb-red)] font-display font-extrabold text-2xl sm:text-4xl px-8 py-6 sm:py-8 rounded-full shadow-2xl">
              <ShoppingBag className="w-8 h-8 sm:w-10 sm:h-10" /> {t("Sipariş Vermek İçin Dokunun")}
            </span>
            <span className="text-white/85 text-base sm:text-lg animate-pulse">{t("Ekrana dokunarak başlayın")}</span>
          </div>
        </button>
      )}

      {/* Kiosk header */}
      <div className="bg-[var(--fb-red)] text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight">{t("Sipariş Vermeye Başla")}</h1>
            <p className="text-white/85 text-sm mt-1 truncate">
              {location ? `${location} · ` : ""}{t("Menüden seç, sepete ekle ve siparişini tamamla.")}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <LangToggle lang={lang} setLang={setLang} className="scale-90 sm:scale-100" />
            <button
              data-testid="open-cart-btn"
              onClick={() => setCartOpen(true)}
              className={`relative flex items-center gap-2 bg-white text-[var(--fb-red)] font-bold px-4 sm:px-5 py-2.5 rounded-full active:scale-95 transition-transform ${cartCount > 0 ? "animate-cart-glow" : ""}`}
            >
              <ShoppingBag key={`icon-${cartCount}`} className={`w-5 h-5 ${cartCount > 0 ? "animate-cart-bump" : ""}`} />
              <span key={`txt-${cartCount}`} className={`hidden sm:inline ${cartCount > 0 ? "animate-cart-bump" : ""}`}>{t("Sepetim")}</span>
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[var(--fb-amber)] text-[var(--fb-ink)] text-xs font-extrabold w-6 h-6 grid place-items-center rounded-full animate-pop">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* İçerik: solda kategoriler */}
      <div className={`max-w-[1400px] mx-auto px-3 sm:px-5 py-4 flex gap-3 sm:gap-4 ${started || publicCode ? "pb-32" : ""}`}>
        <aside data-testid="kiosk-category-sidebar" className="w-24 sm:w-36 shrink-0">
          <div className="sticky top-20 flex flex-col gap-2">
            {cats.map((cat) => (
              <button
                key={cat}
                data-testid={`kiosk-cat-${cat}`}
                onClick={() => {
                  setActiveCat(cat);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                style={{ touchAction: "manipulation" }}
                className={`text-left px-3 py-3 rounded-xl text-sm sm:text-base font-bold border-2 leading-tight transition-colors active:scale-95 ${
                  activeCat === cat
                    ? "bg-[var(--fb-red)] text-white border-[var(--fb-red)]"
                    : "bg-white text-[var(--fb-ink)] border-[var(--fb-border)] hover:border-[var(--fb-red)] hover:text-[var(--fb-red)]"
                }`}
              >
                {m(cat)}
              </button>
            ))}
          </div>
        </aside>
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="text-center py-20 text-[var(--fb-muted)]">{t("Yükleniyor...")}</div>
          ) : filtered.length === 0 ? (
            <div data-testid="kiosk-empty" className="text-center py-20 text-[var(--fb-muted)]">{t("Bu kategoride henüz ürün yok.")}</div>
          ) : (
            <div data-testid="kiosk-product-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-4">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} onAdd={handleAddRequest} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {(started || publicCode) && (
        <div data-testid="kiosk-bottom-cart-bar" className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[var(--fb-border)] shadow-[0_-10px_30px_-10px_rgba(0,0,0,0.2)]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
            <div className="relative w-14 h-14 rounded-2xl bg-red-50 grid place-items-center shrink-0">
              <ShoppingBag key={`bar-${cartCount}`} className={`w-7 h-7 text-[var(--fb-red)] ${cartCount > 0 ? "animate-cart-bump" : ""}`} />
              {cartCount > 0 && (
                <span data-testid="floating-cart-badge" className="absolute -top-2 -right-2 bg-[var(--fb-amber)] text-[var(--fb-ink)] text-xs font-extrabold w-7 h-7 grid place-items-center rounded-full animate-pop">
                  {cartCount}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-[var(--fb-muted)]">{cartCount > 0 ? `${cartCount} ${t("ürün")}` : t("Sepetiniz boş")}</p>
              <p data-testid="bottom-bar-total" className="font-display font-extrabold text-2xl sm:text-3xl text-[var(--fb-ink)] leading-tight">{formatTL(total)}</p>
            </div>
            <button
              data-testid="floating-cart-btn"
              onClick={() => setCartOpen(true)}
              disabled={cartCount === 0}
              className={`flex items-center gap-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-40 text-white font-display font-extrabold text-lg sm:text-xl px-6 sm:px-10 py-4 rounded-full active:scale-95 transition-transform ${cartCount > 0 ? "animate-cart-glow" : ""}`}
            >
              {t("Sepete Git")} <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {removalProduct && (
        <RemovablesModal
          product={removalProduct}
          onClose={() => setRemovalProduct(null)}
          onConfirm={({ removals, added, drink }) => {
            addToCart(removalProduct, removals, added, drink);
            setRemovalProduct(null);
          }}
        />
      )}

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        updateQty={updateQty}
        updateNote={updateNote}
        subtotal={subtotal}
        tax={tax}
        total={total}
        placeOrder={placeOrder}
        clearCart={() => {
          setCart([]);
          setDiscount(null);
        }}
        discount={publicCode ? null : discount}
        discountAmount={discountAmount}
        grossTotal={gross}
        onApplyCode={publicCode ? null : applyCode}
        onRemoveCode={() => setDiscount(null)}
        orderType={orderType}
        onFinished={() => {
          setCartOpen(false);
          setStarted(false);
          setLang("tr");
        }}
      />
    </div>
    </LangContext.Provider>
  );
}
